import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarPlus,
    faClock,
    faComments,
    faHistory,
    faMicrochip,
    faPlay,
    faSyncAlt,
    faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import localForage from "localforage";
import TelehealthShell from "./TelehealthShell";
import { buildApiUrl, getJsonAuthHeaders, useBaseUrl } from "../../../utils/utils";

interface TelehealthSessionItem {
    id: string;
    session_id: string;
    title: string;
    description?: string;
    session_type: string;
    status: "scheduled" | "active" | "completed" | "cancelled";
    scheduled_time: string;
    duration: number;
    recording_enabled: boolean;
    participants_count: number;
    created_by: string;
    collaboration_id: string;
    collaboration_title: string;
    patient_name: string;
    notes?: string;
    room_name: string;
}

interface TelehealthStats {
    total_sessions: number;
    active_sessions: number;
    upcoming_sessions: number;
    total_devices: number;
    online_devices: number;
    monthly_sessions: number;
}

interface DeviceItem {
    device_id: string;
    device_name: string;
    device_type: string;
    status: string;
    last_connected?: string | null;
    last_reading?: Record<string, any> | null;
}

interface CollaborationItem {
    collaboration_id: string;
    title: string;
    description: string;
    status: string;
    patient: {
        id: string;
        name: string;
        contact?: string;
        email?: string;
    };
    stats: {
        telehealth_sessions: number;
    };
}

const formatDateTime = (value: string) => {
    if (!value) return "Not scheduled";
    return new Date(value).toLocaleString();
};

const formatRelativeStatus = (value: string) => {
    const now = Date.now();
    const target = new Date(value).getTime();
    const diffMinutes = Math.round((target - now) / 60000);
    if (Math.abs(diffMinutes) < 60) {
        return diffMinutes >= 0 ? `Starts in ${diffMinutes} min` : `${Math.abs(diffMinutes)} min ago`;
    }
    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) {
        return diffHours >= 0 ? `Starts in ${diffHours} hr` : `${Math.abs(diffHours)} hr ago`;
    }
    const diffDays = Math.round(diffHours / 24);
    return diffDays >= 0 ? `Starts in ${diffDays} day${diffDays === 1 ? "" : "s"}` : `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"} ago`;
};

const TelehealthCenterPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();

    const [clinicId, setClinicId] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState<TelehealthSessionItem[]>([]);
    const [stats, setStats] = useState<TelehealthStats | null>(null);
    const [devices, setDevices] = useState<DeviceItem[]>([]);
    const [collaborations, setCollaborations] = useState<CollaborationItem[]>([]);
    const [search, setSearch] = useState("");
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [formData, setFormData] = useState({
        collaboration_id: "",
        title: "",
        description: "",
        session_type: "video",
        scheduled_date: "",
        scheduled_time: "",
        duration: "30",
        recording_enabled: false,
        notes: "",
    });

    const activeTab = useMemo<"overview" | "history" | "devices" | "messaging">(() => {
        if (location.pathname.endsWith("/history")) return "history";
        if (location.pathname.endsWith("/devices")) return "devices";
        if (location.pathname.endsWith("/messaging")) return "messaging";
        return "overview";
    }, [location.pathname]);

    useEffect(() => {
        document.title = "Telehealth Center - Uzima Healthcare";
        initialize();
    }, []);

    const initialize = async () => {
        try {
            setLoading(true);
            const storedClinicId = await localForage.getItem("clinic_id");
            const loginInfo: any = await localForage.getItem("uzima_login_info");
            const resolvedClinicId = String(storedClinicId || loginInfo?.clinic_id || "");
            setClinicId(resolvedClinicId);
            await Promise.all([
                fetchSessions(resolvedClinicId),
                fetchStats(resolvedClinicId),
                fetchDevices(),
                fetchCollaborations(resolvedClinicId),
            ]);
        } catch (error) {
            console.error("Telehealth initialization error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSessions = async (resolvedClinicId: string) => {
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/telehealth-sessions/?clinic_id=${resolvedClinicId}`), { headers });
            const data = await response.json();
            setSessions(Array.isArray(data?.sessions) ? data.sessions : data?.data?.sessions || []);
        } catch (error) {
            console.error("Error fetching telehealth sessions:", error);
            setSessions([]);
        }
    };

    const fetchStats = async (resolvedClinicId: string) => {
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/telehealth-stats/?clinic_id=${resolvedClinicId}`), { headers });
            const data = await response.json();
            setStats(data?.data || null);
        } catch (error) {
            console.error("Error fetching telehealth stats:", error);
            setStats(null);
        }
    };

    const fetchDevices = async () => {
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, "collaborations/iot-devices/"), { headers });
            const data = await response.json();
            setDevices(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
            console.error("Error fetching telehealth devices:", error);
            setDevices([]);
        }
    };

    const fetchCollaborations = async (resolvedClinicId: string) => {
        try {
            const headers = await getJsonAuthHeaders();
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/collaborations/?clinic_id=${resolvedClinicId}`), { headers });
            const data = await response.json();
            setCollaborations(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
            console.error("Error fetching collaborations:", error);
            setCollaborations([]);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
        }));
    };

    const resetForm = () => {
        setFormData({
            collaboration_id: "",
            title: "",
            description: "",
            session_type: "video",
            scheduled_date: "",
            scheduled_time: "",
            duration: "30",
            recording_enabled: false,
            notes: "",
        });
    };

    const closeScheduleModal = () => {
        setShowScheduleModal(false);
        resetForm();
    };

    const handleCreateSession = async () => {
        if (!formData.collaboration_id || !formData.title || !formData.scheduled_date || !formData.scheduled_time) {
            Swal.fire({ icon: "warning", title: "Missing details", text: "Please choose a collaboration, title, date, and time." });
            return;
        }

        try {
            setCreating(true);
            const headers = await getJsonAuthHeaders();
            const scheduled = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
            const response = await fetch(buildApiUrl(baseUrl, "collaborations/telehealth-sessions/"), {
                method: "POST",
                headers,
                body: JSON.stringify({
                    collaboration_id: formData.collaboration_id,
                    title: formData.title,
                    description: formData.description,
                    session_type: formData.session_type,
                    scheduled_time: scheduled.toISOString(),
                    duration: parseInt(formData.duration, 10),
                    recording_enabled: formData.recording_enabled,
                    notes: formData.notes,
                }),
            });
            const data = await response.json();
            if (!response.ok || data.status !== "success") {
                throw new Error(data.message || "Unable to create telehealth session");
            }
            await initialize();
            closeScheduleModal();
            Swal.fire({
                icon: "success",
                title: "Session created",
                text: "Your telehealth session is ready. You can join it immediately or from the sessions list.",
                confirmButtonColor: "#0f766e",
            }).then(() => {
                if (data.session_id) {
                    navigate(`/institution/telehealth/session/${data.session_id}`);
                }
            });
        } catch (error: any) {
            console.error("Create telehealth session error:", error);
            Swal.fire({ icon: "error", title: "Creation failed", text: error?.message || "Failed to create telehealth session." });
        } finally {
            setCreating(false);
        }
    };

    const filteredSessions = useMemo(() => {
        return sessions.filter((session) => {
            const haystack = `${session.title} ${session.description || ""} ${session.patient_name} ${session.collaboration_title}`.toLowerCase();
            return !search || haystack.includes(search.toLowerCase());
        });
    }, [sessions, search]);

    const liveSessions = filteredSessions.filter((session) => session.status === "active");
    const upcomingSessions = filteredSessions.filter((session) => session.status === "scheduled");
    const historySessions = filteredSessions.filter((session) => ["completed", "cancelled"].includes(session.status));
    const selectedCollaboration = collaborations.find((item) => item.collaboration_id === formData.collaboration_id);

    const renderScheduleModal = () => (
        <div className="sc-modal-overlay" onClick={closeScheduleModal}>
            <div className="sc-modal sc-modal-lg" onClick={(event) => event.stopPropagation()}>
                <div className="sc-modal-header">
                    <h2>Schedule telehealth session</h2>
                    <button className="sc-modal-close" onClick={closeScheduleModal}><i className="bx bx-x"></i></button>
                </div>
                <div className="sc-modal-body">
                    <div className="sc-form-group">
                        <label>Collaboration <span>*</span></label>
                        <select className="sc-input" name="collaboration_id" value={formData.collaboration_id} onChange={handleInputChange}>
                            <option value="">Select collaboration</option>
                            {collaborations.map((collaboration) => (
                                <option key={collaboration.collaboration_id} value={collaboration.collaboration_id}>
                                    {collaboration.title} - {collaboration.patient.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {selectedCollaboration && (
                        <div className="sc-panel" style={{ marginBottom: 18 }}>
                            <div className="sc-panel-body">
                                <strong>{selectedCollaboration.title}</strong>
                                <p style={{ margin: "6px 0 0", fontSize: "0.82rem", color: "var(--uzima-shell-text-muted)" }}>
                                    Patient: {selectedCollaboration.patient.name} · Existing telehealth sessions: {selectedCollaboration.stats?.telehealth_sessions || 0}
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="sc-form-row">
                        <div className="sc-form-group">
                            <label>Session title <span>*</span></label>
                            <input className="sc-input" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g. Cardiology follow-up" />
                        </div>
                        <div className="sc-form-group">
                            <label>Session type</label>
                            <select className="sc-input" name="session_type" value={formData.session_type} onChange={handleInputChange}>
                                <option value="video">Video conference</option>
                                <option value="audio">Audio conference</option>
                                <option value="consultation">Consultation</option>
                            </select>
                        </div>
                    </div>
                    <div className="sc-form-group">
                        <label>Description</label>
                        <textarea className="sc-textarea" name="description" value={formData.description} onChange={handleInputChange} rows={3} placeholder="What is this session for?" />
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group">
                            <label>Date <span>*</span></label>
                            <input className="sc-input" type="date" name="scheduled_date" value={formData.scheduled_date} onChange={handleInputChange} min={new Date().toISOString().split("T")[0]} />
                        </div>
                        <div className="sc-form-group">
                            <label>Time <span>*</span></label>
                            <input className="sc-input" type="time" name="scheduled_time" value={formData.scheduled_time} onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="sc-form-row">
                        <div className="sc-form-group">
                            <label>Duration (minutes)</label>
                            <select className="sc-input" name="duration" value={formData.duration} onChange={handleInputChange}>
                                <option value="15">15</option>
                                <option value="30">30</option>
                                <option value="45">45</option>
                                <option value="60">60</option>
                                <option value="90">90</option>
                            </select>
                        </div>
                        <div className="sc-form-group">
                            <label>Notes</label>
                            <input className="sc-input" name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Clinical notes or agenda" />
                        </div>
                    </div>
                    <label className="sc-toggle">
                        <input type="checkbox" name="recording_enabled" checked={formData.recording_enabled} onChange={handleInputChange} />
                        <span>Enable meeting recording metadata for this session</span>
                    </label>
                </div>
                <div className="sc-modal-footer">
                    <button className="sc-btn sc-btn-ghost" onClick={closeScheduleModal}>Cancel</button>
                    <button className="sc-btn sc-btn-primary" onClick={handleCreateSession} disabled={creating}>
                        <FontAwesomeIcon icon={faCalendarPlus} /> {creating ? "Creating..." : "Create & open session"}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderOverview = () => (
        <>
            <div className="sc-stats">
                <div className="sc-stat">
                    <div className="sc-stat-icon teal"><i className="bx bx-video"></i></div>
                    <div className="sc-stat-value">{stats?.total_sessions ?? sessions.length}</div>
                    <div className="sc-stat-label">Total Sessions</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon blue"><i className="bx bx-broadcast"></i></div>
                    <div className="sc-stat-value">{stats?.active_sessions ?? liveSessions.length}</div>
                    <div className="sc-stat-label">Live Meetings</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon amber"><i className="bx bx-calendar"></i></div>
                    <div className="sc-stat-value">{stats?.upcoming_sessions ?? upcomingSessions.length}</div>
                    <div className="sc-stat-label">Upcoming Sessions</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon violet"><i className="bx bx-chip"></i></div>
                    <div className="sc-stat-value">{stats?.online_devices ?? 0}</div>
                    <div className="sc-stat-label">Online Devices</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon rose"><i className="bx bx-line-chart"></i></div>
                    <div className="sc-stat-value">{stats?.monthly_sessions ?? 0}</div>
                    <div className="sc-stat-label">Monthly Sessions</div>
                </div>
            </div>

            <div className="sc-toolbar">
                <div className="sc-search">
                    <i className="bx bx-search"></i>
                    <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by session title, patient, or collaboration..." />
                </div>
                <span className="sc-chip soft">Hosted Jitsi is powering live 2-way meetings</span>
            </div>

            <div className="sc-split">
                <div className="sc-panel">
                    <div className="sc-panel-head">
                        <div>
                            <h3>Live & upcoming sessions</h3>
                            <p>Launch live meetings or join scheduled sessions from one queue.</p>
                        </div>
                    </div>
                    <div className="sc-panel-body">
                        <div className="sc-list-stack">
                            {[...liveSessions, ...upcomingSessions].slice(0, 8).map((session) => (
                                <div className="sc-list-item" key={session.session_id}>
                                    <div>
                                        <strong>{session.title}</strong>
                                        <small style={{ display: "block", marginTop: 4 }}>
                                            {session.patient_name} · {session.collaboration_title} · {formatDateTime(session.scheduled_time)}
                                        </small>
                                        <small style={{ display: "block", marginTop: 4, color: "var(--uzima-shell-accent)" }}>{formatRelativeStatus(session.scheduled_time)}</small>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                        <span className={`sc-chip ${session.status === "active" ? "" : "soft"}`}>{session.status === "active" ? "Live" : "Scheduled"}</span>
                                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={() => navigate(`/institution/telehealth/session/${session.session_id}`)}>
                                            <FontAwesomeIcon icon={faPlay} /> {session.status === "active" ? "Join now" : "Open room"}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {liveSessions.length === 0 && upcomingSessions.length === 0 && (
                            <div className="sc-empty">
                                <i className="bx bx-video-off"></i>
                                <h3>No sessions yet</h3>
                                <p>Create your first collaboration-linked telehealth meeting to start holding live consultations.</p>
                                <button className="sc-btn sc-btn-primary" onClick={() => setShowScheduleModal(true)}><FontAwesomeIcon icon={faCalendarPlus} /> Schedule first session</button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="sc-panel">
                    <div className="sc-panel-head">
                        <div>
                            <h3>Collaboration launchpad</h3>
                            <p>Pick an active collaboration and schedule the next video consultation.</p>
                        </div>
                    </div>
                    <div className="sc-panel-body">
                        <div className="sc-list-stack">
                            {collaborations.slice(0, 6).map((collaboration) => (
                                <div className="sc-list-item" key={collaboration.collaboration_id}>
                                    <div>
                                        <strong>{collaboration.title}</strong>
                                        <small style={{ display: "block", marginTop: 4 }}>{collaboration.patient.name} · {collaboration.stats?.telehealth_sessions || 0} telehealth sessions</small>
                                    </div>
                                    <button
                                        className="sc-btn sc-btn-outline sc-btn-sm"
                                        onClick={() => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                collaboration_id: collaboration.collaboration_id,
                                                title: prev.title || `${collaboration.title} Session`,
                                            }));
                                            setShowScheduleModal(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCalendarPlus} /> Schedule
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const renderHistory = () => (
        <div className="sc-panel">
            <div className="sc-panel-head">
                <div>
                    <h3>Session history</h3>
                    <p>Review completed and cancelled meetings across all collaborations.</p>
                </div>
            </div>
            <div className="sc-panel-body">
                {historySessions.length === 0 ? (
                    <div className="sc-empty">
                        <i className="bx bx-history"></i>
                        <h3>No session history yet</h3>
                        <p>Completed and cancelled telehealth sessions will appear here once meetings start happening.</p>
                    </div>
                ) : (
                    <div className="sc-table-wrap">
                        <table className="sc-table">
                            <thead>
                                <tr>
                                    <th>Session</th>
                                    <th>Patient</th>
                                    <th>When</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {historySessions.map((session) => (
                                    <tr key={session.session_id}>
                                        <td>
                                            <strong>{session.title}</strong>
                                            <div style={{ fontSize: "0.74rem", color: "var(--uzima-shell-text-muted)", marginTop: 4 }}>{session.collaboration_title}</div>
                                        </td>
                                        <td>{session.patient_name}</td>
                                        <td>{formatDateTime(session.scheduled_time)}</td>
                                        <td><span className={`sc-chip ${session.status === "completed" ? "" : "soft"}`}>{session.status}</span></td>
                                        <td><button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => navigate(`/institution/telehealth/session/${session.session_id}`)}>View</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const renderDevices = () => (
        <div className="sc-panel">
            <div className="sc-panel-head">
                <div>
                    <h3>Connected telehealth devices</h3>
                    <p>Monitor device readiness for remote consultations and connected readings.</p>
                </div>
            </div>
            <div className="sc-panel-body">
                {devices.length === 0 ? (
                    <div className="sc-empty">
                        <i className="bx bx-chip"></i>
                        <h3>No connected devices</h3>
                        <p>Connected telehealth devices will appear here once they are linked to a collaboration workflow.</p>
                    </div>
                ) : (
                    <div className="sc-kpi-grid">
                        {devices.map((device) => (
                            <div className="sc-kpi-card" key={device.device_id}>
                                <h4>{device.device_name}</h4>
                                <strong>{device.status}</strong>
                                <span>{device.device_type.replace(/_/g, " ")}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderMessaging = () => (
        <div className="sc-split">
            <div className="sc-panel">
                <div className="sc-panel-head">
                    <div>
                        <h3>Meeting coordination</h3>
                        <p>Use collaboration context to prepare participants, timing, and clinical notes before the call.</p>
                    </div>
                </div>
                <div className="sc-panel-body">
                    <div className="sc-list-stack">
                        {collaborations.slice(0, 8).map((collaboration) => (
                            <div className="sc-list-item" key={collaboration.collaboration_id}>
                                <div>
                                    <strong>{collaboration.title}</strong>
                                    <small style={{ display: "block", marginTop: 4 }}>{collaboration.patient.name} · {collaboration.patient.email || "No email"}</small>
                                </div>
                                <button className="sc-btn sc-btn-outline sc-btn-sm" onClick={() => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        collaboration_id: collaboration.collaboration_id,
                                        title: `${collaboration.title} Session`,
                                    }));
                                    setShowScheduleModal(true);
                                }}>
                                    <FontAwesomeIcon icon={faComments} /> Prepare session
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="sc-panel">
                <div className="sc-panel-head">
                    <div>
                        <h3>How chat works now</h3>
                        <p>Hosted Jitsi handles in-meeting messaging directly inside the live room.</p>
                    </div>
                </div>
                <div className="sc-panel-body">
                    <div className="sc-list-stack">
                        <div className="sc-list-item">
                            <div>
                                <strong>During the meeting</strong>
                                <small style={{ display: "block", marginTop: 4 }}>Participants can use the Jitsi chat panel for real-time discussion.</small>
                            </div>
                        </div>
                        <div className="sc-list-item">
                            <div>
                                <strong>Before the meeting</strong>
                                <small style={{ display: "block", marginTop: 4 }}>Use notes and collaboration context when creating the telehealth session.</small>
                            </div>
                        </div>
                        <div className="sc-list-item">
                            <div>
                                <strong>After the meeting</strong>
                                <small style={{ display: "block", marginTop: 4 }}>Session history keeps a record of the scheduled event and its collaboration link.</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <TelehealthShell
                title="Telehealth Center"
                description="Schedule remote consultations, launch secure video meetings, and manage telehealth workflows from one place."
                activeTab={activeTab}
                action={
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button className="sc-btn sc-btn-outline" onClick={initialize}>
                            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
                        </button>
                        <button className="sc-btn sc-btn-primary" onClick={() => setShowScheduleModal(true)}>
                            <FontAwesomeIcon icon={faCalendarPlus} /> New Session
                        </button>
                    </div>
                }
            >
                {loading ? (
                    <div className="sc-spinner"></div>
                ) : !clinicId ? (
                    <div className="sc-empty">
                        <i className="bx bx-buildings"></i>
                        <h3>No clinic context found</h3>
                        <p>Please sign in with a clinic account before managing telehealth sessions.</p>
                    </div>
                ) : activeTab === "overview" ? renderOverview() : activeTab === "history" ? renderHistory() : activeTab === "devices" ? renderDevices() : renderMessaging()}
            </TelehealthShell>
            {showScheduleModal && renderScheduleModal()}
        </>
    );
};

export default TelehealthCenterPage;
